import React from "react";
import { Link, useLocation } from "react-router-dom";
import { X, User } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useSubmenuSidebar } from "@/contexts/SubmenuSidebarContext";
import { useSidebarSearch } from "@/contexts/SidebarSearchContext";
import { useNavigationPreferences } from "@/hooks/useNavigationPreferences";
import { usePersonas } from "@/hooks/usePersonas";
import { useCustomBrandFeatures } from "@/hooks/useCustomBrandFeatures";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useClientPortalToken } from "@/hooks/useClientPortalToken";
import type { MenuNode } from "@/config/menuStructure";
import { cn } from "@/lib/utils";
import { useTypedTranslation } from "@/lib/i18n/hooks";
import type { NavigationKey } from "@/lib/i18n/types";
import { MainMenuItemPin } from "@/components/navigation/MainMenuItemPin";

const SUBMENU_SIDEBAR_WIDTH = "16rem";

function getMenuTranslationKey(menuId: string): string | null {
  const translationMap: Record<string, string> = {
    "360": "360RealEstate",
    "360-dashboards-group": "360DashboardsGroup",
    "360-dashboard": "360Dashboard",
    "360-my-activity": "360MyActivity",
    "360-control-tower-group": "360ControlTowerGroup",
    "360-property-report": "360PropertyReport",
    "360-unidades-group": "360UnidadesGroup",
    "360-units": "360Units",
    "360-projects": "360Projects",
    "360-clientes-group": "360ClientesGroup",
    "360-customers": "360Customers",
    "360-propuestas-group": "360PropuestasGroup",
    "360-proposals": "360Proposals",
    "360-eventos-group": "360EventosGroup",
    "360-events": "360Events",
    "360-visits": "360Visits",
    "360-external-chatbot": "360ExternalChatbot",
    "360-campanas-group": "360CampanasGroup",
    "360-campaigns": "360Campaigns",
    "360-embudos-group": "360EmbudosGroup",
    "360-buyer-funnel": "360BuyerFunnel",
    "360-owner-funnel": "360OwnerFunnel",
    "360-loyalty-funnel": "360LoyaltyFunnel",
    "360-operaciones-group": "360OperacionesGroup",
    "360-operations": "360Operations",
    "360-usuarios-group": "360UsuariosGroup",
    "360-users": "360Users",
    "learning-center": "learningCenter",
    "lms-dashboard": "lmsDashboard",
    "lms-analytics": "lmsAnalytics",
    "lms-coursemanagement": "lmsCoursemanagement",
    "lms-instructors": "lmsInstructors",
    "lms-course-types": "lmsCourseTypes",
    "lms-associations": "lmsAssociations",
    "lms-academies": "lmsAcademies",
    "lms-leads": "lmsLeads",
    "lms-student-eligibility": "lmsStudentEligibility",
    "lms-company-enrollments": "lmsCompanyEnrollments",
    "lms-enrollments": "lmsEnrollments",
    "lms-feedback": "lmsFeedback",
    "lms-ask-for-more": "lmsAskForMore",
    "lms-email-analytics": "lmsEmailAnalytics",
    "lms-campaigns": "lmsCampaigns",
    "lms-shares": "lmsShares",
    "lms-quiz-management": "lmsQuizManagement",
    "lms-quiz-templates": "lmsQuizTemplates",
    "lms-quiz-submissions": "lmsQuizSubmissions",
    "lms-quiz-performance": "lmsQuizPerformance",
    "lms-quiz-styles": "lmsQuizStyles",
    "brand-basics": "brandBasics",
    "brand-shared": "brandShared",
    "brand-the-team": "brandTheTeam",
    "brand-product-services": "brandProductServices",
    "brand-team": "brandTeam",
    "growth-squad": "growthSquad",
    "tasks-projects": "tasksProjects",
    "tasks-current-brand": "tasksCurrentBrand",
    "tasks-across-brands": "tasksAcrossBrands",
    "tasks-sprints": "tasksSprints",
    "my-tasks": "myTasks",
    "my-team-dashboard": "myTeamDashboard",
    "my-tasks-all-brands": "myTasksAllBrands",
    "tasks-monitoring-all-brands": "tasksMonitoringAllBrands",
    "my-overall-dashboard": "myOverallDashboard",
  };
  return translationMap[menuId] ?? null;
}

export function SubmenuSidebar() {
  const { openNode, closeSubmenu } = useSubmenuSidebar();
  const { searchQuery } = useSidebarSearch();
  const location = useLocation();
  const { preferences } = useNavigationPreferences();
  const { personas, isLoading: personasLoading } = usePersonas();
  const { hasFeature, isLoading: featuresLoading } = useCustomBrandFeatures();
  const { currentBrandId } = useBrandManager();
  const { token: clientPortalToken } = useClientPortalToken(currentBrandId);
  const { tNav } = useTypedTranslation();
  const { state } = useSidebar();

  // Close submenu when main sidebar collapses
  React.useEffect(() => {
    if (state === 'collapsed' && openNode) {
      closeSubmenu();
    }
  }, [state, openNode, closeSubmenu]);

  const isItemVisible = (itemId: string): boolean => {
    if (!preferences?.items) return true;
    const pref = preferences.items.find((p) => p.id === itemId);
    return pref?.visible ?? true;
  };

  const hasVisibleChildren = (item: MenuNode): boolean => {
    if (item.featureKey && (featuresLoading || !hasFeature(item.featureKey as any))) return false;
    if (item.id === "report-client-portal") return !!clientPortalToken && isItemVisible(item.id);
    if (!item.children?.length) return isItemVisible(item.id);
    return item.children.some((c) => {
      if (c.featureKey && (featuresLoading || !hasFeature(c.featureKey as any))) return false;
      return isItemVisible(c.id) && hasVisibleChildren(c);
    });
  };

  const getTranslatedLabel = (item: MenuNode): string => {
    const key = getMenuTranslationKey(item.id);
    if (key) {
      try {
        const translated = tNav(key as NavigationKey);
        return translated !== key ? translated : item.label;
      } catch {
        return item.label;
      }
    }
    return item.label;
  };

  // Default order for Content (content-marketing) children: Content Calendar first, SETTINGS last
  const CONTENT_MARKETING_CHILDREN_ORDER = new Map<string, number>([
    ['content-calendar', 0],
    ['long-form-content', 1],
    ['short-form-content', 2],
    ['avatar-announcements', 3],
    ['interactive-polls', 4],
    ['lead-magnets', 5],
    ['interactive-quizzes', 6],
    ['interactive-mini-games', 7],
    ['content-settings', 8],
  ]);

  // Default order for Support Portal children: Help Center first, SETTINGS last
  const SUPPORT_PORTAL_CHILDREN_ORDER = new Map<string, number>([
    ['support-portal-main', 0],
    ['support-portal-settings', 1],
  ]);

  const getItemOrder = (itemId: string): number => {
    const contentOrder = CONTENT_MARKETING_CHILDREN_ORDER.get(itemId);
    if (contentOrder !== undefined) {
      if (preferences?.items) {
        const pref = preferences.items.find((p) => p.id === itemId);
        if (typeof pref?.order === "number") return pref.order;
      }
      return contentOrder;
    }
    const supportOrder = SUPPORT_PORTAL_CHILDREN_ORDER.get(itemId);
    if (supportOrder !== undefined) {
      if (preferences?.items) {
        const pref = preferences.items.find((p) => p.id === itemId);
        if (typeof pref?.order === "number") return pref.order;
      }
      return supportOrder;
    }
    if (preferences?.items) {
      const pref = preferences.items.find((p) => p.id === itemId);
      if (typeof pref?.order === "number") return pref.order;
    }
    return 0;
  };

  const sortByOrder = (items: MenuNode[]): MenuNode[] =>
    [...items].sort((a, b) => getItemOrder(a.id) - getItemOrder(b.id));

  // Filter submenu tree by search query (same logic as main sidebar)
  const filterMenuItems = (items: MenuNode[], query: string): MenuNode[] => {
    if (!query.trim()) return items;
    const lowerQuery = query.toLowerCase();
    return items
      .map((item) => {
        const translatedLabel = getTranslatedLabel(item);
        const labelMatch = translatedLabel?.toLowerCase().includes(lowerQuery);
        const pathMatch = item.path?.toLowerCase().includes(lowerQuery);
        const descriptionMatch = item.description?.toLowerCase().includes(lowerQuery);
        const itemMatches = labelMatch || pathMatch || descriptionMatch;
        if (item.children?.length) {
          const filteredChildren = filterMenuItems(item.children, query);
          if (itemMatches || filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }
          return null;
        }
        return itemMatches ? item : null;
      })
      .filter((item): item is MenuNode => item !== null);
  };

  const renderSubmenuItem = (item: MenuNode, level: number): React.ReactNode => {
    if (item.featureKey && (featuresLoading || !hasFeature(item.featureKey as any))) return null;
    if (item.children?.length) {
      if (!hasVisibleChildren(item)) return null;
    } else {
      if (!isItemVisible(item.id)) return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isActive = item.path
      ? location.pathname === item.path ||
        (item.path !== "/" && location.pathname.startsWith(item.path + "/"))
      : false;
    const Icon = item.icon;

    const renderIcon = () => {
      if (!Icon) return null;
      if (typeof Icon === "string") return <img src={Icon} alt="" className="h-4 w-4 object-contain" />;
      return <Icon className="h-4 w-4 text-primary" />;
    };

    const levelPadding = level === 0 ? "" : level === 1 ? "pl-5" : level === 2 ? "pl-10" : "pl-[60px]";

    if (item.path && !hasChildren) {
      if (item.id === "report-client-portal") {
        if (!clientPortalToken) return null;
        const linkPath = `/client/${clientPortalToken}`;
        const portalActive = location.pathname === linkPath || location.pathname.startsWith(linkPath + "/");
        return (
          <SidebarMenuItem key={item.id}>
            <SidebarMenuButton asChild isActive={portalActive}>
              <Link to={linkPath} className={cn("flex items-center gap-2", levelPadding)}>
                {renderIcon()}
                <span>{getTranslatedLabel(item)}</span>
                <MainMenuItemPin path={linkPath} />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }
      const needsBrandId =
        (item.path.startsWith("/courses") || item.path === "/learn/login") && currentBrandId;
      const linkPath = needsBrandId
        ? `${item.path}${item.path.includes("?") ? "&" : "?"}brand_id=${currentBrandId}`
        : item.path;
      return (
        <SidebarMenuItem key={item.id}>
          <SidebarMenuButton asChild isActive={isActive}>
            <Link to={linkPath} className={cn("flex items-center gap-2", levelPadding)}>
              {renderIcon()}
              <span>{getTranslatedLabel(item)}</span>
              <MainMenuItemPin path={linkPath} />
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    if (!item.path && !hasChildren) {
      const fallbackPath = "/surveys";
      return (
        <SidebarMenuItem key={item.id}>
          <SidebarMenuButton asChild>
            <Link to={fallbackPath} className={cn("flex items-center gap-2", levelPadding)}>
              {renderIcon()}
              <span>{getTranslatedLabel(item)}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    if (hasChildren) {
      const visibleChildren = sortByOrder(item.children!).filter((c) => {
        if (c.id === "report-client-portal") return !!clientPortalToken && isItemVisible(c.id);
        return isItemVisible(c.id);
      });
      if (visibleChildren.length === 0) return null;

      // All section headers: same pattern as Learning Center / 360 – bold, uppercase, no icon
      return (
        <SidebarGroup key={item.id} className="px-0 py-1">
          <SidebarGroupLabel
            className={cn(
              "text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 py-1",
              levelPadding
            )}
          >
            {getTranslatedLabel(item)}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleChildren.map((child) => renderSubmenuItem(child, level + 1))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      );
    }

    return null;
  };

  if (!openNode || !openNode.children?.length) return null;

  // SETTINGS always last in subsidebar: any item whose id ends with -settings or label is SETTINGS
  const isSettingsItem = (node: MenuNode): boolean =>
    node.id.endsWith("-settings") || node.label === "SETTINGS";

  // Subsidebar order: enforce explicit order for Content and Support Portal so Content Calendar is first / SETTINGS last
  const getOrderForSubsidebar = (itemId: string): number => {
    const contentOrder = CONTENT_MARKETING_CHILDREN_ORDER.get(itemId);
    if (contentOrder !== undefined) return contentOrder;
    const supportOrder = SUPPORT_PORTAL_CHILDREN_ORDER.get(itemId);
    if (supportOrder !== undefined) return supportOrder;
    return 999;
  };

  const sorted = sortByOrder(openNode.children);
  let ordered: typeof openNode.children;
  if (openNode.id === "content-marketing" || openNode.id === "support-portal") {
    ordered = [...openNode.children].sort((a, b) => getOrderForSubsidebar(a.id) - getOrderForSubsidebar(b.id));
  } else if (openNode.id === "reporting-hub") {
    ordered = [...sorted.filter((c) => c.id !== "reports-public-path"), ...sorted.filter((c) => c.id === "reports-public-path")];
  } else {
    // Generic rule: SETTINGS always last for all other menus
    ordered = [...sorted.filter((c) => !isSettingsItem(c)), ...sorted.filter((c) => isSettingsItem(c))];
  }
  const baseChildren = ordered.filter((c) =>
    c.children?.length ? hasVisibleChildren(c) : isItemVisible(c.id)
  );
  const visibleChildren = searchQuery.trim()
    ? filterMenuItems(baseChildren, searchQuery)
    : baseChildren;

  // For Synthetic Personas submenu: group personas by brief for the dynamic list
  const isPersonasSubmenu = openNode?.id === "synthetic-personas";
  const personasByBrief = isPersonasSubmenu
    ? personas.reduce((acc, persona) => {
        const briefName = persona.brief?.name || "Uncategorized";
        const q = searchQuery.trim().toLowerCase();
        if (q && !briefName.toLowerCase().includes(q) && !persona.name.toLowerCase().includes(q)) return acc;
        if (!acc[briefName]) acc[briefName] = [];
        acc[briefName].push(persona);
        return acc;
      }, {} as Record<string, typeof personas>)
    : {};
  const hasPersonasList = isPersonasSubmenu && !personasLoading && Object.keys(personasByBrief).length > 0;

  // Fixed viewport panel
  const submenuPanel = (
    <div
      className="fixed inset-y-0 z-10 flex h-svh flex-col overflow-hidden border-l border-sidebar-border bg-sidebar text-sidebar-foreground"
      style={{
        width: SUBMENU_SIDEBAR_WIDTH,
        left: "var(--sidebar-width, 16rem)",
      }}
    >
      <div className="shrink-0 flex items-center justify-between gap-2 border-b border-sidebar-border px-3 py-2">
        <span className="truncate text-sm font-semibold">{getTranslatedLabel(openNode)}</span>
        <button
          type="button"
          onClick={closeSubmenu}
          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          aria-label="Close submenu"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {visibleChildren.length === 0 ? (
        <div className="flex-1 min-h-0 overflow-y-auto p-4 text-center text-sm text-muted-foreground">
          No menu items found matching &quot;{searchQuery}&quot;
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto py-2">
          <SidebarGroup className="px-0">
            <SidebarGroupContent>
              <SidebarMenu className="gap-0">
                {visibleChildren.map((child) => renderSubmenuItem(child, 0))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          {hasPersonasList && (
            <SidebarGroup className="px-0 pt-2 mt-2 border-t border-sidebar-border">
              <SidebarGroupLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2 py-1">
                Personas
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-0">
                  {Object.entries(personasByBrief).map(([briefName, briefPersonas]) => (
                    <div key={briefName}>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                        {briefName}
                      </div>
                      {briefPersonas.map((persona) => (
                        <SidebarMenuItem key={persona.id}>
                          <SidebarMenuButton
                            asChild
                            isActive={location.pathname === `/personas/${persona.id}`}
                            tooltip={persona.name}
                          >
                            <Link to={`/personas/${persona.id}`} className="flex items-center gap-2 pl-5">
                              <User className="h-4 w-4 text-primary shrink-0" />
                              <span className="truncate">{persona.name}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </div>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Spacer so main content doesn't shift; matches fixed panel width */}
      <div
        className="shrink-0 h-svh"
        style={{ width: SUBMENU_SIDEBAR_WIDTH }}
        aria-hidden
      />
      {submenuPanel}
    </>
  );
}
